import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { ChevronDown } from 'lucide-react';
import { useOutsideHandler } from 'src/common/hooks';

import { ActiveAccount } from '../types';
import { pluralizeAccounts, pluralizeExcluded } from '../utils';

type ActiveAccountsChecklistProps = {
  accounts: ActiveAccount[];
  excludedIds: string[];
  onChange: (excludedIds: string[]) => void;
  disabled?: boolean;
};

const ActiveAccountsChecklist: React.FC<ActiveAccountsChecklistProps> = ({
  accounts,
  excludedIds,
  onChange,
  disabled = false,
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => setIsOpen(false), []);
  useOutsideHandler(containerRef, close);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const excludedSet = useMemo(() => new Set(excludedIds), [excludedIds]);

  const visibleAccounts = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) {
      return accounts;
    }
    return accounts.filter(
      (a) => a.name.toLowerCase().includes(normalized) || String(a.id) === normalized
    );
  }, [accounts, query]);

  const toggle = (accountId: string): void => {
    if (excludedSet.has(accountId)) {
      onChange(excludedIds.filter((id) => id !== accountId));
    } else {
      onChange([...excludedIds, accountId]);
    }
  };

  const setVisibleIncluded = (included: boolean): void => {
    const visibleIds = visibleAccounts.map((a) => a.id);
    const rest = excludedIds.filter((id) => !visibleIds.includes(id));
    onChange(included ? rest : [...rest, ...visibleIds]);
  };

  const excludedCount = accounts.filter((a) => excludedSet.has(a.id)).length;
  const includedCount = accounts.length - excludedCount;

  return (
    <div ref={containerRef} className="space-y-2">
      <button
        ref={triggerRef}
        type="button"
        className="input flex items-center justify-between w-full text-sm text-left"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span>
          {pluralizeAccounts(accounts.length)} · {pluralizeExcluded(excludedCount)}
        </span>
        <ChevronDown
          size={16}
          className={classNames('text-gray-400 transition-transform', { 'rotate-180': isOpen })}
          aria-hidden="true"
        />
      </button>
      {isOpen && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>
              <span className="font-semibold text-gray-700">{includedCount}</span> de{' '}
              {accounts.length} cuentas activas recibirán el email
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                className="hover:underline disabled:opacity-50 text-blue-700"
                disabled={disabled}
                onClick={() => setVisibleIncluded(true)}
              >
                Marcar todas
              </button>
              <button
                type="button"
                className="hover:underline disabled:opacity-50 text-blue-700"
                disabled={disabled}
                onClick={() => setVisibleIncluded(false)}
              >
                Desmarcar todas
              </button>
            </div>
          </div>
          <input
            className="input w-full text-sm"
            placeholder="Buscar cuenta por nombre o ID…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="max-h-60 overflow-y-auto border border-gray-200 rounded">
            {visibleAccounts.length === 0 && (
              <p className="px-3 py-2 text-xs text-gray-400">No hay cuentas que coincidan.</p>
            )}
            {visibleAccounts.map((a) => (
              <label
                key={a.id}
                className="flex items-center gap-2 px-3 py-1.5 text-sm border-b last:border-0 cursor-pointer hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  checked={!excludedSet.has(a.id)}
                  disabled={disabled}
                  onChange={() => toggle(a.id)}
                />
                <span className={excludedSet.has(a.id) ? 'text-gray-400 line-through' : ''}>
                  {a.name}
                </span>
                <span className="ml-auto text-xs text-gray-400">#{a.id}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActiveAccountsChecklist;
